import { useState, useEffect, type MouseEvent, JSX } from "react";

import { ArrowDropDown, ArrowDropUp, Public } from "@mui/icons-material";
import { Button, Menu, MenuItem, Theme, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { languagesList } from "../../../config/languages";
import AppLanguage from "../../../constants/appLanguage.enum";
import useAppLanguageStore from "../../../store/lang.store";

const getStyles = (theme: Theme) => ({
  languageButton: { borderColor: theme.palette.border.default, textTransform: "none", backgroundColor: theme.palette.surface.card },
  languageMenu: {
    "aria-labelledby": "language-menu-toggle-button",
  },
});

function LangBox(): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const { appLanguage, setAppLanguage } = useAppLanguageStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (): void => {
    setAnchorEl(null);
  };
  const handleSelect = async (langShort: AppLanguage): Promise<void> => {
    setAppLanguage(langShort);
    handleClose();
  };

  useEffect(() => {
    document.documentElement.dir = "ltr";
    document.documentElement.lang = appLanguage;
  }, [appLanguage]);

  return (
    <>
      <Button
        id='language-menu-toggle-button'
        aria-controls={open ? "language-menu" : undefined}
        aria-haspopup='true'
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        color='secondary'
        endIcon={open ? <ArrowDropUp /> : <ArrowDropDown />}
        variant='outlined'
        startIcon={<Public />}
        disableRipple
        disableElevation
        disableFocusRipple
        disableTouchRipple
        sx={getStyles(theme).languageButton}
      >
        {t(appLanguage.toUpperCase())}
      </Button>
      <Menu id='language-menu' anchorEl={anchorEl} open={open} onClose={handleClose} MenuListProps={getStyles(theme).languageMenu}>
        {languagesList.map((lang) => (
          <MenuItem
            className='medium'
            key={lang.short}
            dense
            onClick={() => {
              void handleSelect(lang.short);
            }}
          >
            {t(lang.name)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default LangBox;
